import React, { Fragment, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import stores from '../../../models/Stores';

// Components
import { GISBoxVM } from './GISBoxVM';
import { MapComp } from '../Maps/Map';
import { EditSoilAndLand } from '../EditSoilAndLand/EditSoilAndLand';
import { Metadata } from '../Metadata/Metadata';
import { FlatButton, SecondaryButton } from 'nrcs-design-system';
import { GISHeader } from '../GISHeader';
import { GISFooter } from '../GISFooter';
import { mdiPencilBoxOutline, mdiInformationOutline, mdiAlertOctagon, mdiCamera } from '@mdi/js';
import { Modal, ModalHeader, ModalFooter, ModalBody, Container, Row, Col, Button } from 'reactstrap';
import { ProjectTitle } from '../../Projects/ProjectTitle';
import { UserLeavingWarning } from 'cd-eft/components/common/UserLeavingWarning/UserLeavingWarning';
import { WatershedAnalysisDetails, LULCDetails, HSGDetails } from './WADetails';
import { LoaderWithBar } from '../../common/LoadingSpinner/LoadingSpinner';

// Common
import { CollapseMenu } from '../../common/CollapseMenu/CollapseMenu';
import { Notification } from '../../common/Notification/Notification';
import { StepIndicator } from '../../common/StepIndicator/StepIndicator';
import { LoadingSpinner, ToolBarLoadingSpinner } from '../../common/LoadingSpinner/LoadingSpinner';
import { Basemap } from '../Maps/Basemap/Basemap';
import { ComboBox } from 'cd-eft/components/common/ComboBox';
import { StaticTooltip } from 'cd-eft/components/common/StaticTooltip/StaticTooltip';

// Styles
import './gisBox.scss';
import { DelineateWatershedInput } from '../DelineateWatershedInput';
import { PrimaryButton } from 'nrcs-design-system';
import Icon from '@mdi/react';
import { mdiOpenInNew } from '@mdi/js';
import { BasicDataForm } from '../BasicDataForm';
import { Form, Label, Input } from 'reactstrap';
import { Popup } from 'cd-eft/components/common/Popup';
import { PendingEditManager } from 'cd-eft/components/EFH2/PendingEditManager/PendingEditManager';
import { InformationModal } from 'cd-eft/components/common/InformationModal/InformationModal';
import { ScreenshotPreview } from 'cd-eft/components/common/Screenshot/ScreenshotPreview';
import { ScreenshotPreviewVM } from 'cd-eft/components/common/Screenshot/ScreenshotPreviewVM';

const GISBoxComponent = (props: any) => {
  const screenshotPreview = useRef<ScreenshotPreviewVM>(new ScreenshotPreviewVM()).current;
  const history = useHistory();
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen(!isOpen);
  const vmREF = useRef<GISBoxVM>();

  if (!vmREF.current) {
    vmREF.current = new GISBoxVM(props, history);
  }
  const vm = vmREF.current;

  const [isMetadataModalVisible, setIsMetadataModalVisible] = useState(false);

  const toggleIsMetadataModelVisible = () => {
    setIsMetadataModalVisible(!isMetadataModalVisible);
  };

  React.useEffect(() => {
    try { vm.interruptInAppNavigation(window); } catch (err) { console.error(err) }
    vm.initialize()
      .then()
      .catch((reason) => {
        // if the initialize encounters an unhandled exception, what should the program do?
        vm.isLoading = false;
        //alert('There was a problem initializing the page.'); //todo replace with an error modal
        console.error('GisBoxVM.initialize()', reason);
        //ideally, the user will never see the error Modal
        vm.errorModalMessage = '';
        vm.showErrorModal = true;
      });
  }, []);

  useEffect(
    function mount() {
      function unmount() {
        // console.log('unmount beforeunload event listener');
        window.removeEventListener('beforeunload', (event) => vm.unloadEventListener(event));
      }
      return unmount();
    }, []
  );

  useEffect(() => {
    vm.setUncommittedEdits();
  }, [
    vm.drawings.pendingEditCount
  ]);

  //for button disabled 
  if (vm.errorMessage) {
    vm.disableSaveComment = true
  }

  return (
    <div className="eft-gisbox">
      <ScreenshotPreview vm={screenshotPreview}></ScreenshotPreview>
      <Popup
        trigger={vm.userLeavingConfirmation}
        setTrigger={vm.userLeavingWarningOnCancel}
        title={`Leaving Single Watershed Analysis`}
        handleOK={vm.userLeavingWarningOnOk}
        handleCancel={vm.userLeavingWarningOnCancel}
        cancelText='Close'
        okText='Ok'
      >
        Changes you made may not be saved.
      </Popup>
      <GISHeader />
      <Modal className="gis-loader" isOpen={vm.isLoading} centered={true}>
        <ModalBody>
          {/* <LoadingSpinner message={vm.gisLoadingMessage} loading={vm.isLoading} /> */}
          {vm.isLoading && (
            <LoaderWithBar
              message={vm.gisLoadingMessage}
              className={'gis-loader'}
              processName={vm.processName}
              loadingProgress={vm.loadingProgress}
              loading={vm.isLoading}
            />
          )}
        </ModalBody>
      </Modal>
      {/*Notes Modal */}
      <Modal isOpen={vm.showNotesModal} toggle={vm.handleToggleNotes} size="lg" centered={true}>
        <ModalHeader>
          Add Design Comment (Optional)
          <div className=" gis-dropdown">
            <StaticTooltip
              icon={mdiInformationOutline}
              tooltipMessage='Add any feedback that can provide guidance 
              over the reasons behind some of the 
              edits that were made to the input fields.'
              tooltipSide='right'
              id="project-info-button"
              extraClass="information-button gis-icon static-tooltip-from-element"
              aria-label="additional comment details"
            />
          </div>

        </ModalHeader>

        <Container className="notes-container margin-top-205">
          <Form>
            <Row>
              <Col xs="6">
                <ComboBox
                  className="GIS-form-select"
                  onChange={vm.handleSelectInputChange}
                  initValue={vm.activityViewModel?.currentStepName}
                  value={vm.projectSelectedInput}
                  valid={true}
                  idAndName='GIS_selection'
                  label='Workflow Step'
                  //value={vm.activityViewModel?.currentStepName}
                  errorMsg=''
                  options={vm.noteOption}
                  NoInitialBlankOption={true}
                />
              </Col>

              <Col xs="6">
                <Input
                  type="textarea"
                  rows="6"
                  name="note"
                  id="note"
                  data-testid="input"
                  onChange={vm.handleCommentInputChange}
                />
                <Label className="usa-label notes-label" for="note">
                  Limited to 1000 characters.
                </Label>
                <span className="usa-error-message" role="alert">
                  {vm.errorMessage}
                </span>
              </Col>
            </Row>
          </Form>
          <Row>
            <Col xs="12">
              <p>Examples:</p>
              <ul>
                <li>I modified the soil category.</li>
                <li>I edited the land use polygons.</li>
              </ul>
            </Col>
          </Row>
        </Container>
        <ModalFooter>
          <button className="SecondaryButton usa-button usa-button--outline"
            onClick={() => { vm.handleToggleNotes(); }}
          >
            <strong> Close</strong>
          </button>
          <button className="PrimaryButton usa-button"
            onClick={() => { vm.addComment(); }}
            disabled={vm.disableSaveComment}
          >
            <strong>Add</strong>
          </button>
        </ModalFooter>
      </Modal>
      {/*Notes Modal End*/}

      {/* Error Modal */}
      <Modal isOpen={vm.showErrorModal} centered={true}>
        <ModalHeader>
          <Icon path={mdiAlertOctagon} title="Error" size={2} color="red" />
          <strong>Application Error</strong>
        </ModalHeader>
        <Container className="notes-container margin-top-205">
          <Row>
            <Col xs="12">
              <p>
                Uh oh, you weren't supposed to see this. There was a problem loading the project you selected. Please
                return to the Projects page and try loading your project again.
              </p>
              <p>{vm.errorModalMessage}</p>
            </Col>
          </Row>
        </Container>
        <ModalFooter>
          <PrimaryButton
            className="usa-button"
            onClick={() => {
              //clear the current project information, it is likely the cause of the issue
              stores.AppStore.efh2Store.projectsStore.reset();
              //send user back to the projects page
              history.replace('/projects');
            }}
          >
            Return to Projects
          </PrimaryButton>
        </ModalFooter>
      </Modal>

      {vm.activityViewModel?.currentStepIndex !== 4 && (
        <FlatButton
          className="info-button"
          aria-label="Metadata"
          onClick={toggleIsMetadataModelVisible}
          iconOnly={mdiInformationOutline}
          title="Metadata"
        />
      )}

      {vm.activityViewModel?.currentStepIndex !== 4 && (
        <FlatButton
          className="screenshot-button"
          aria-label="Take Screenshot"
          onClick={() => { vm.takeCurrentScreenshot(screenshotPreview); }}
          iconOnly={mdiCamera}
          title="Take Screenshot"
        />
      )}

      {vm.activityViewModel ? (
        <Fragment>
          <div
            className={
              vm.activityViewModel.currentStepIndex === 4 && !vm.activityViewModel.landUseTableComplete
                ? 'gis-toolbar gis-control-toolbar'
                : 'gis-toolbar gis-control-toolbar hsg-toolbar'
            }
          >
            {/*First 4 Steps */}
            <div hidden={vm.activityViewModel.currentStepIndex > 2}>
              <CollapseMenu
                title={vm.activityViewModel.currentStepName}
                cardBodyId="upload-dem"
                noIcon={true}
                forceOpen={true}
                showAsHeader={true}
                footer={
                  <GISFooter vm={vm} footerMessage={vm.footerMessage} mapDrawMethods={vm.mapDrawMethods}></GISFooter>
                }
                titleClass='collapse-title-span'
                sectionHelp={true}
                sectionHelpCallback={() => vm.openHelpModal(vm.activityViewModel!.currentStepName, <WatershedAnalysisDetails title={vm.activityViewModel!.currentStepName} />)}
              >
                <DelineateWatershedInput vm={vm.activityViewModel} />
                <ToolBarLoadingSpinner
                  message={vm.activityViewModel.jobMessages[vm.activityViewModel.jobMessages.length - 1]}
                  jobExecuting={vm.activityViewModel.jobExecuting}
                />
              </CollapseMenu>
            </div>
            {/*First 4 Steps END*/}

            {/* Edit Land Use  */}
            {vm.activityViewModel.currentStepIndex === 3 && !vm.activityViewModel.landUseTableComplete && (
              <div>
                <CollapseMenu
                  title="Edit Land Use"
                  noIcon={true}
                  cardBodyId="prep-sal"
                  forceOpen={true}
                  showAsHeader={true}
                  footer={
                    <GISFooter vm={vm} footerMessage={vm.footerMessage} mapDrawMethods={vm.mapDrawMethods}></GISFooter>
                  }
                  titleClass='collapse-title-span'
                  sectionHelp={true}
                  sectionHelpCallback={() => vm.openHelpModal(vm.activityViewModel!.currentStepName, <LULCDetails className='LULC_detail' />)}
                >
                  {!vm.activityViewModel.jobExecuting ? (
                    <EditSoilAndLand
                      landUseTableComplete={vm.activityViewModel.landUseTableComplete}
                      soilArray={vm.activityViewModel.salSoilArray}
                      landArray={vm.activityViewModel.salLandArray}
                      callback={vm.SALEditCallback}
                      view={vm.view}
                      onCheckboxRowChanged={vm.onCheckboxRowChanged}
                      currentStepIndex={vm.activityViewModel.currentStepIndex}
                      activityViewModel={vm.activityViewModel}
                    />
                  ) : (
                    <ToolBarLoadingSpinner
                      message={vm.activityViewModel.jobMessages[vm.activityViewModel.jobMessages.length - 1]}
                      jobExecuting={vm.activityViewModel?.jobExecuting}
                    />
                  )}
                </CollapseMenu>
              </div>
            )}
            {/* Edit Land Use End */}

            {/* Select HSG */}
            {vm.activityViewModel.currentStepIndex === 3 && vm.activityViewModel.landUseTableComplete && (
              <div>
                <CollapseMenu
                  title="Select HSG"
                  noIcon={true}
                  cardBodyId="prep-sal"
                  forceOpen={true}
                  showAsHeader={true}
                  footer={
                    <GISFooter vm={vm} footerMessage={vm.footerMessage} mapDrawMethods={vm.mapDrawMethods}></GISFooter>
                  }
                  titleClass='collapse-title-span'
                  sectionHelp={true}
                  sectionHelpCallback={() => vm.openHelpModal(vm.activityViewModel!.currentStepName, <HSGDetails className='HSG_detail' />)}
                >
                  {!vm.activityViewModel.jobExecuting ? (
                    <EditSoilAndLand
                      landUseTableComplete={vm.activityViewModel.landUseTableComplete}
                      soilArray={vm.activityViewModel.salSoilArray}
                      landArray={vm.activityViewModel.salLandArray}
                      callback={vm.SALEditCallback}
                      view={vm.view}
                      onCheckboxRowChanged={vm.onCheckboxRowChanged}
                      currentStepIndex={vm.activityViewModel.currentStepIndex}
                      activityViewModel={vm.activityViewModel}
                    />
                  ) : (
                    <ToolBarLoadingSpinner
                      message={vm.activityViewModel.jobMessages[vm.activityViewModel.jobMessages.length - 1]}
                      jobExecuting={vm.activityViewModel?.jobExecuting}
                    />
                  )}
                </CollapseMenu>
              </div>
            )}

            {/* Select HSG  End  */}
          </div>
        </Fragment>
      ) : null}

      <div className="row" hidden={vm.activityViewModel && vm.activityViewModel.currentStepIndex === 4}>
        <div className="col-md-12">
          <div id={stores.AppStore.efh2Store.projectsStore.currentProject.projectTitle ? 'mapViewProject' : 'mapView'}>
            <PendingEditManager vm={vm} mapView={vm.view}
              activeTab={'Measure'} />
            <MapComp
              map={vm.setMap}
              view={vm.setView}
              goHome={vm.activityViewModel?.zoomToSubjectArea.bind(vm.activityViewModel)}
            />
            <Basemap view={vm.view} />
          </div>
        </div>
      </div>

      <div className="top-bar">
        {vm.activityViewModel ? (
          <>
            <StepIndicator
              stepList={vm.activityViewModel.stepNames}
              currentStep={vm.activityViewModel.currentStepName}
            />
          </>
        ) : null}
      </div>
      <ProjectTitle currentStep={vm.activityViewModel?.currentStepIndex} demName={vm.activityViewModel?.demSelectName} watershedAcreage={vm.activityViewModel?.watershedAcreage} />

      {/*Metadata Modal */}
      <Modal
        isOpen={isMetadataModalVisible}
        toggle={toggleIsMetadataModelVisible}
        size="lg"
        centered={true}
        className="eft-action-modal metadata-modal"
      >
        <ModalHeader toggle={toggleIsMetadataModelVisible}>
          <strong className="margin-left-105">Metadata</strong>
          <a className="margin-left-105" href="/analysis/hydrology/metadata" target="_blank">
            <Icon path={mdiOpenInNew} title="Expand" size={0.8} color="#005ea2" />
          </a>
        </ModalHeader>
        <Container>
          <Metadata isOpen={false} />
        </Container>
      </Modal>
      {/*Metadata Modal End*/}

      <LoadingSpinner
        message=""
        loading={vm.loadingResults && !vm.activityViewModel?.jobExecuting}
        outerClassName="basic-data-loader"
      />
      {vm.activityViewModel?.currentStepIndex === 4 && !vm.isLoading && !vm.loadingResults ? (
        <BasicDataForm activityViewModel={vm.activityViewModel} createGISFilesLink={vm.createGISFilesLink} openHelpModal={vm.openHelpModal} />
      ) : null}

      {/* Help Modal */}
      <InformationModal
        showHelpModal={vm.showHelpModal}
        closeHelpModal={vm.closeHelpModal}
        helpModalTitle={vm.helpModalTitle}
        helpModalContent={vm.helpModalContent}
      />
      {/* Help Modal End */}

      <Notification
        type={vm.notification.type}
        message={vm.notification.message}
        header={vm.notification.header}
        hidden={vm.notification.hidden}
        timestamp={vm.notification.timestamp}
      />

      {(vm.uncommittedEdits > 0) && (
        <UserLeavingWarning
          when={vm.uncommittedEdits > 0}
          section={vm.drawingNotification.title}
          onOK={vm.userLeavingDrawingOnOk}
          text={vm.uncommittedEdits > 0
            ? vm.drawingNotification.message
            : ''
          }
        />
      )}

      {/* Download Spinner */}
      <LoadingSpinner
        message="Downloading Data ..."
        loading={vm.isDownloading}
        className='projects-loader'
      />
    </div>
  );
};

export const GISBox = observer(GISBoxComponent);
